import React, { useMemo, useState } from 'react';
import { RADIO_REFERENCE_NOTE, RADIO_REFERENCE_SOURCE, RADIO_ZONES } from './radioReferenceData';
import './radio-reference.css';

function zoneLabel(zone) {
  return String(zone?.name || `ZONE ${zone?.zone || ''}`).replace(/^ZONE\s+\d+[-\s]*/i, '').trim() || `Zone ${zone?.zone}`;
}

function cleanValue(value) {
  const text = String(value || '').trim();
  return text && !/^n\/?a$/i.test(text) ? text : '—';
}

function searchText(zone, channel) {
  return [
    `zone ${zone.zone}`,
    zone.zone,
    zone.name,
    zone.description,
    `channel ${channel.channel}`,
    channel.channel,
    channel.display,
    channel.rxFreq,
    channel.rxTone,
    channel.txFreq,
    channel.txTone,
    channel.power,
    channel.wideNarrow,
    channel.notes,
  ].map(value => String(value || '').toLowerCase()).join(' ');
}

export default function RadioReference({ offline = false }) {
  const [selectedZone, setSelectedZone] = useState(10);
  const [query, setQuery] = useState('');
  const terms = useMemo(() => query.trim().toLowerCase().split(/\s+/).filter(Boolean), [query]);

  const activeZone = useMemo(
    () => RADIO_ZONES.find(zone => zone.zone === Number(selectedZone)) || RADIO_ZONES[0],
    [selectedZone],
  );

  const searchResults = useMemo(() => {
    if (!terms.length) return [];
    const results = [];
    RADIO_ZONES.forEach(zone => {
      zone.channels.forEach(channel => {
        const haystack = searchText(zone, channel);
        if (terms.every(term => haystack.includes(term))) results.push({ zone, channel });
      });
    });
    return results;
  }, [terms]);

  const groupedResults = useMemo(() => {
    const groups = [];
    searchResults.forEach(result => {
      let group = groups.find(item => item.zone.zone === result.zone.zone);
      if (!group) {
        group = { zone: result.zone, channels: [] };
        groups.push(group);
      }
      group.channels.push(result.channel);
    });
    return groups;
  }, [searchResults]);

  return (
    <div className="radio-ref-root">
      <div className="radio-ref-meta">
        <div>
          <span className="radio-ref-kicker">BK KNG P150 / M150 · {offline ? 'OFFLINE READY' : 'FIELD REFERENCE'}</span>
          <h3>Statewide radio banks</h3>
          <p>Search by bank, channel, display name, RX/TX frequency, tone/NAC/DPL, or published notes. No network connection is required once the app is installed.</p>
        </div>
        <span className="radio-ref-offline">{offline ? 'Offline' : 'Stored in app'}</span>
      </div>

      <div className="radio-ref-source">
        <strong>{RADIO_REFERENCE_SOURCE}</strong>
        <span>{RADIO_REFERENCE_NOTE}</span>
      </div>

      <div className="radio-ref-controls">
        <label className="radio-ref-search">
          <span>Search radio reference</span>
          <input
            type="search"
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="168.625, 110.9, NAC 293, VFIRE 22, Zone 23…"
            autoComplete="off"
          />
        </label>
        {!terms.length && (
          <label className="radio-ref-zone-select">
            <span>Radio bank / zone</span>
            <select value={selectedZone} onChange={event => setSelectedZone(Number(event.target.value))}>
              {RADIO_ZONES.map(zone => (
                <option key={zone.zone} value={zone.zone}>Zone {zone.zone} — {zoneLabel(zone)}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      {terms.length ? (
        <div className="radio-ref-results">
          <div className="radio-ref-result-count">{searchResults.length} channel match{searchResults.length === 1 ? '' : 'es'} across {groupedResults.length} bank{groupedResults.length === 1 ? '' : 's'}</div>
          {groupedResults.map(group => (
            <section className="radio-ref-bank" key={group.zone.zone}>
              <div className="radio-ref-bank-head">
                <div>
                  <span>ZONE {group.zone.zone}</span>
                  <strong>{zoneLabel(group.zone)}</strong>
                  {group.zone.description && <small>{group.zone.description}</small>}
                </div>
                <button type="button" onClick={() => { setQuery(''); setSelectedZone(group.zone.zone); }}>Open bank</button>
              </div>
              <ChannelList channels={group.channels} />
            </section>
          ))}
          {!searchResults.length && <div className="radio-ref-empty">No programmed radio entries match “{query.trim()}”.</div>}
        </div>
      ) : (
        <section className="radio-ref-bank">
          <div className="radio-ref-bank-head">
            <div>
              <span>ZONE {activeZone.zone}</span>
              <strong>{zoneLabel(activeZone)}</strong>
              {activeZone.description && <small>{activeZone.description}</small>}
            </div>
            <div className="radio-ref-count">{activeZone.channels.length} channel{activeZone.channels.length === 1 ? '' : 's'}</div>
          </div>
          <ChannelList channels={activeZone.channels} />
        </section>
      )}

      <p className="radio-ref-footnote">Reference reflects the supplied 2023 statewide programming document. Frequency/tone information is a field lookup aid; use the current incident Communications Plan / ICS 205 whenever assigned programming differs.</p>
    </div>
  );
}

function ChannelList({ channels }) {
  return (
    <div className="radio-ref-channel-list">
      <div className="radio-ref-channel-header">
        <span>CH</span><span>DISPLAY</span><span>RX</span><span>RX TONE</span><span>TX</span><span>TX TONE</span>
      </div>
      {channels.map((channel, index) => (
        <article className="radio-ref-channel" key={`${channel.channel}-${channel.display}-${index}`}>
          <div className="radio-ref-channel-main">
            <span className="radio-ref-channel-number">{channel.channel}</span>
            <strong>{channel.display}</strong>
            <div className="radio-ref-flags">
              {channel.power && <span>{channel.power === 'H' ? 'HIGH' : channel.power === 'L' ? 'LOW' : channel.power}</span>}
              {channel.wideNarrow && <span>{channel.wideNarrow === 'N' ? 'NARROW' : channel.wideNarrow === 'W' ? 'WIDE' : channel.wideNarrow}</span>}
            </div>
          </div>
          <div className="radio-ref-channel-programming">
            <div><small>RX FREQ</small><b>{cleanValue(channel.rxFreq)}</b></div>
            <div><small>RX TONE</small><b>{cleanValue(channel.rxTone)}</b></div>
            <div><small>TX FREQ</small><b>{cleanValue(channel.txFreq)}</b></div>
            <div><small>TX TONE</small><b>{cleanValue(channel.txTone)}</b></div>
          </div>
          {channel.notes && <div className="radio-ref-channel-notes">{channel.notes}</div>}
        </article>
      ))}
      {!channels.length && <div className="radio-ref-empty">No channel entries are listed for this bank.</div>}
    </div>
  );
}
